import { Injectable } from '@angular/core';
import { ServiceModel } from '@app/core/models';

import { createStore, setProps, withProps } from '@ngneat/elf';
import {
  deleteAllEntities,
  getEntity,
  selectActiveEntities,
  selectAllEntities,
  selectEntity,
  selectManyByPredicate,
  setActiveIds,
  upsertEntities,
  withActiveIds,
  withEntities,
} from '@ngneat/elf-entities';
import { joinRequestResult, withRequestsStatus } from '@ngneat/elf-requests';
import { Observable, map } from 'rxjs';
import { CategoriesRepository } from './categories.repository';

export interface ServicesProps {
  meta: {
    lastRefresh: Date | null;
  };
}

const store = createStore(
  { name: 'services' },
  withProps<ServicesProps>({
    meta: { lastRefresh: null },
  }),
  withEntities<ServiceModel, '_id'>({ idKey: '_id' }),
  withRequestsStatus(),
  withActiveIds(),
);

@Injectable({ providedIn: 'root' })
export class ServicesRepository {
  constructor(private categoriesRepo: CategoriesRepository) {}

  public services$ = store.pipe(
    selectAllEntities(),
    joinRequestResult(['services']),
  );
  public activeServices$: Observable<ServiceModel[] | undefined> = store.pipe(
    selectActiveEntities(),
  );

  public setServices(services: ServiceModel[]) {
    store.update(deleteAllEntities());

    if (services && services.length > 0) {
      services.sort((a, b) => a.name.localeCompare(b.name));
      store.update(upsertEntities(services));
      const categoriesIds = [
        ...new Set(
          services.flatMap((service) =>
            service.categories.map((category) => category._id),
          ),
        ),
      ];

      this.categoriesRepo.setActiveCategories(categoriesIds);
    } else {
      this.categoriesRepo.setActiveCategories([]);
    }
  }

  public getByKinds(kinds: string[]) {
    return store.pipe(
      selectManyByPredicate((service) => kinds.includes(service.serviceKind)),
    );
  }

  public getByTags(tags: string[]) {
    return store.pipe(
      selectManyByPredicate((service) =>
        tags.some((tag) => service.tags.includes(tag)),
      ),
    );
  }

  public getAllByCategoryId(categoryId: string) {
    return store.pipe(
      selectManyByPredicate((service) =>
        service.categories.some((category) => category._id === categoryId),
      ),
    );
  }

  public getAllByCategoryKey(categoryKey: string) {
    return store.pipe(
      selectManyByPredicate((service) =>
        service.categories.some((category) => category.key === categoryKey),
      ),
    );
  }

  public getAllByCategoryIdFiltered(categoryId: string, providedAt: string) {
    return store.pipe(
      selectManyByPredicate(
        (service) =>
          service.categories.some((category) => category._id === categoryId) &&
          service.providedAt === providedAt,
      ),
    );
  }

  public getById(_id: string) {
    return store.pipe(selectEntity(_id));
  }

  public getByIdStatic(_id: string): ServiceModel | undefined {
    return store.query(getEntity(_id));
  }

  public setActiveServices(categoryId: string) {
    store
      .pipe(
        selectAllEntities(),
        map((services: ServiceModel[]) =>
          services
            .filter((service) =>
              service.categories.some(
                (category) => category._id === categoryId,
              ),
            )
            .map((service) => service._id),
        ),
      )
      .subscribe((ids) => {
        store.update(setActiveIds(ids));
      });
  }

  public clearServicesAndCategories(): void {
    store.update(deleteAllEntities());
    this.categoriesRepo.setActiveCategories([]);
    store.update(setProps({ meta: { lastRefresh: null } }));
  }

  public clear(): void {
    store.update(deleteAllEntities());
    store.update(setProps({ meta: { lastRefresh: null } }));
  }
}
